<template>
  <div v-if="isLoading" class="flex items-center justify-center h-40 rounded-2xl">
    <i v-html="$icons.loading"></i>
  </div>
  <div v-else class="overflow-hidden rounded-2xl" style="box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.8)">
    <div class="overflow-x-auto overflow-y-auto">
      <table class="bg-ontick-graphite-500 divide-ontick-steel-500 min-w-full divide-y">
        <thead class="bg-ontick-steel-500">
          <tr>
            <th
              v-for="(headerStrat, index) in headerStrategy"
              :key="headerStrat.key"
              :class="{
                'w-20': index === 0,
              }"
            >
              <button
                :class="{ 'sr-only': headerStrat.hidden }"
                :disabled="!headerStrat.sortable"
                @click="sort(headerStrat.key)"
              >
                <span>
                  {{ headerStrat.label }}
                </span>
                <span v-if="sort_key == headerStrat.key">
                  {{ sortIcon }}
                </span>
              </button>
            </th>
          </tr>
        </thead>

        <tbody>
          <tr v-if="!getHistoryTradeIdeas.length">
            <td colspan="9999">
              <div class="h-96 w-full flex items-center justify-center">
                <p class="text-base font-medium text-gray-300">Nenhuma trade idea encontrada</p>
              </div>
            </td>
          </tr>

          <!-- ================================================== -->

          <tr v-for="strat in sortedData" :key="strat.ti_id">
            <!-- ID -->
            <td>{{ strat.ti_id }}</td>

            <!-- Operação -->
            <td>
              <div class="flex flex-col items-start gap-1">
                <div class="flex items-center gap-2">
                  <!-- Ícone do ativo -->
                  <img :src="strat.ti_symbol_badge" :alt="strat.ti_symbol" class="w-5 h-5 rounded-sm" />

                  <!-- Ativo -->
                  <span
                    class="text-xs font-medium bg-ontick-blue-900 rounded-sm flex items-center justify-center w-[54px] h-5"
                  >
                    {{ strat.ti_symbol }}
                  </span>

                  <!-- Compra (C) / Venda (V) -->
                  <span
                    v-tippy="{ theme: 'ontick', arrow: true }"
                    :content="strat.ti_direction === 'BUY' ? 'Compra' : 'Venda'"
                    class="w-5 h-5 flex items-center justify-center rounded-sm bg-ontick-yellow-800 text-ontick-graphite-500 text-xs font-bold"
                  >
                    {{ strat.ti_direction === "BUY" ? "C" : "V" }}
                  </span>
                </div>
              </div>
            </td>

            <!-- Status -->
            <td>
              {{ tradeIdeasTypes.status(strat.ti_status) }}
            </td>

            <!-- Estratégia -->
            <td>
              <div class="flex gap-2 items-center">
                <img :src="strat.partner_badge" :alt="strat.partner_name" class="w-5 h-5" />
                <!-- <ontick-broker-logo-component :broker="strat.partner_name" /> -->
                <div class="flex flex-col gap-0.5">
                  <p>{{ strat.strategy_name }}</p>
                  <p class="text-xs text-gray-500">{{ strat.strategy_analyst }}</p>
                </div>
              </div>
            </td>

            <!-- Entrada -->
            <td>
              <div class="flex flex-col gap-1">
                <p v-tippy="{ theme: 'ontick', arrow: true }" :content="strat.to_info.to_entry_price" class="text-sm">
                  {{ strat.to_info.to_entry_price | currency }}
                </p>
                <p
                  v-tippy="{ theme: 'ontick', arrow: true }"
                  :content="'Volume da operação: ' + Number(strat.to_info.to_volume).toLocaleString('pt-BR')"
                  class="text-xs text-gray-500 leading-none"
                >
                  {{ Number(strat.to_info.to_volume).toLocaleString("pt-BR") }}
                </p>
              </div>
            </td>

            <!-- TP -->
            <td>
              <div class="flex flex-col gap-1">
                <p class="text-sm">{{ strat.ti_take_profit_price | currency }}</p>
                <p class="text-xs text-gray-500 leading-none">
                  {{ Number(strat.ti_take_profit_percent).toLocaleString("pt-BR") }}%
                </p>
              </div>
            </td>

            <!-- SL -->
            <td>
              <div class="flex flex-col gap-1">
                <p class="text-sm">{{ strat.ti_stop_loss_price | currency }}</p>
                <p class="text-xs text-gray-500 leading-none">
                  {{ Number(strat.ti_stop_loss_percent).toLocaleString("pt-BR") }}%
                </p>
              </div>
            </td>

            <!-- Resultado -->
            <td>
              <template v-if="strat.to_info.to_pnl != null">
                <div class="flex items-center gap-2">
                  <i v-html="$icons.play_green" v-if="strat.to_info.to_pnl > 0"></i>
                  <i v-html="$icons.play_red" v-else-if="strat.to_info.to_pnl < 0"></i>
                  <i v-else>
                    <div class="h-3 w-3 rounded-full bg-gray-500"></div>
                  </i>
                  <span
                    v-tippy="{ theme: 'ontick', arrow: true }"
                    :content="strat.to_info.to_pnl"
                    class="text-gray-300"
                    >{{ strat.to_info.to_pnl | currency }}</span
                  >
                </div>
                <span
                  v-if="strat.to_info.to_perc_result"
                  v-tippy="{ theme: 'ontick', arrow: true }"
                  :content="strat.to_info.to_perc_result"
                  class="text-xs text-gray-500"
                  >({{ Number(strat.to_info.to_perc_result).toLocaleString("pt-BR") }}%)</span
                >
                <span v-else class="text-gray-500 text-sm font-semibold">-</span>
              </template>
              <span v-else class="text-gray-500 text-sm font-semibold">-</span>
            </td>

            <!-- Detalhes -->
            <td>
              <button @click="$emit('details', strat)">
                <svg width="17" height="18" viewBox="0 0 17 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M4.01784 13.6062H10.3199V12.2312H4.01784V13.6062ZM4.01784 9.6875H13.322V8.3125H4.01784V9.6875ZM4.01784 5.76875H13.322V4.39375H4.01784V5.76875ZM1.79492 17.25C1.42826 17.25 1.10742 17.1125 0.832422 16.8375C0.557422 16.5625 0.419922 16.2417 0.419922 15.875V2.125C0.419922 1.75833 0.557422 1.4375 0.832422 1.1625C1.10742 0.8875 1.42826 0.75 1.79492 0.75H15.5449C15.9116 0.75 16.2324 0.8875 16.5074 1.1625C16.7824 1.4375 16.9199 1.75833 16.9199 2.125V15.875C16.9199 16.2417 16.7824 16.5625 16.5074 16.8375C16.2324 17.1125 15.9116 17.25 15.5449 17.25H1.79492ZM1.79492 15.875H15.5449V2.125H1.79492V15.875ZM1.79492 2.125V15.875V2.125Z"
                    fill="#0694E6"
                  />
                </svg>
              </button>
            </td>
          </tr>

          <!-- ================================================== -->
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import OntickSwitchComponent from "../../../shared/components/basic/ontick-switch.component.vue";
import OntickBrokerLogoComponent from "../../../shared/components/basic/ontick-broker-logo.component.vue";
import { tradeIdeasTypes } from "../utils-trade-ideas";

export default {
  name: "TableHistory",

  components: {
    OntickSwitchComponent,
    OntickBrokerLogoComponent,
  },

  data() {
    return {
      tradeIdeasTypes,

      // Ordenação
      sort_order: "desc",
      sort_key: "ti_id",

      // Cabeçalho da tabela de estratégias
      headerStrategy: [
        { label: "ID", key: "id", sortable: false },
        { label: "Operação", key: "operacao", sortable: false },
        { label: "Status", key: "status", sortable: false },
        { label: "Estratégia", key: "estrategia", sortable: false },
        { label: "Entrada", key: "entrada", sortable: false },
        { label: "TP", key: "tp", sortable: false },
        { label: "SL", key: "sl", sortable: false },
        { label: "Resultado", key: "resultado", sortable: false },
        { label: "", key: "info", sortable: false },
      ],
    };
  },

  computed: {
    ...mapGetters({
      getHistoryTradeIdeas: "tradeIdeas/getHistoryTradeIdeas",
      isLoading: "tradeIdeas/isLoading",
    }),

    sortIcon() {
      const directions = {
        asc: "▲",
        desc: "▼",
      };
      // return directions[this.sortDirection] || "";
      return directions[this.sort_order] || "▲";
    },

    sortedData() {
      return this.getHistoryTradeIdeas.slice().sort((a, b) => {
        let modifier = 1;
        if (this.sort_order === "desc") modifier = -1;
        if (a[this.sort_key] < b[this.sort_key]) return -1 * modifier;
        if (a[this.sort_key] > b[this.sort_key]) return 1 * modifier;
        return 0;
      });
    },
  },

  methods: {
    ...mapActions({
      listHistoryTradeIdeas: "tradeIdeas/listHistoryTradeIdeas",
    }),

    sort(key) {
      this.sort_key = key;
      this.sort_order = this.sort_order == "asc" ? "desc" : "asc";
    },
  },

  created() {
    this.listHistoryTradeIdeas();
  },
};
</script>

<style lang="scss" scoped>
.no-divide {
  border-top-width: 0 !important;
}

th {
  @apply py-2 px-3 text-left;
  button {
    @apply text-sm font-semibold text-gray-300;
  }
}

th,
td {
  border-style: solid !important;
}

tbody {
  @apply text-sm font-normal bg-ontick-graphite-500 relative;

  tr {
    @apply relative;
    @apply odd:bg-ontick-graphite-500 even:bg-ontick-carbon-500;

    td {
      @apply px-3 py-4 text-gray-300;
    }
  }
}

.fade {
  &-enter,
  &-leave-to {
    @apply scale-125 opacity-0;
  }
  &-enter-active,
  &-leave-active {
    @apply transition-all duration-300 ease-in-out;
  }
}

/* width */
::-webkit-scrollbar {
  width: 4px;
  height: 3px;
}

/* Track */
::-webkit-scrollbar-track {
  background-color: #2d3748;
}

/* Handle */
::-webkit-scrollbar-thumb {
  @apply rounded-full;
  background-color: #0694e6;
}

/* Handle on hover */
::-webkit-scrollbar-thumb:hover {
  background: #555;
}
</style>
